import React, { useEffect, useState } from 'react';

import { formatLargeNumber, inRn } from 'lib/helpers/utility';

import Button from '../Button/Button';
import Input from '../Input/Input';
import Typography from '../Typography/Typography';

const CustomPagination = ({
  page,
  perPage,
  pageCountOverride,
  totalItems,
  onPageChange = (_page) => {}
}) => {
  const [localPage, setLocalPage] = useState(page);
  const totalPages = Math.ceil(pageCountOverride || totalItems / perPage);

  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;

  const checkIfPageIsLargerThanTotal = () => {
    return page > totalPages ? totalPages : page;
  };

  page = checkIfPageIsLargerThanTotal(); // Found a bug where we can filter if we are based on a page where the items fetched cannot reach that fill out of pages
  // therefore we return the page to it's more extend point i.e TotalPages | CONSULT BEFORE REMOVING!

  const handlePrevious = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  };

  const handleFirst = () => {
    onPageChange(1);
  };

  const handleLast = () => {
    onPageChange(totalPages);
  };

  useEffect(() => {
    setLocalPage(page);
  }, [page]);

  const commitPage = (rawValue) => {
    const parsed = Number(inRn(rawValue, 1));
    const min = 1;
    const max = Math.max(1, totalPages || 1);
    const clamped = Number.isFinite(parsed) ? Math.min(Math.max(parsed, min), max) : min;
    setLocalPage(clamped);
    onPageChange(clamped);
  };

  const handlePageOnBlur = (event) => {
    commitPage(event?.target?.value);
  };

  const handlePageChange = (event) => {
    const rawValue = event?.target?.value;
    const numericValue = inRn(rawValue);

    const upperBound = Math.max(1, totalPages || 1);
    const clamped = Math.min(Number(numericValue), upperBound);
    setLocalPage(clamped);
  };

  return (
    <div className="flex h-full items-center justify-center gap-3">
      <div className="flex items-center gap-1">
        <Typography
          variant="label"
          size="small"
          weight="medium"
          color="neutral-700"
          className="leading-4">
          Page
        </Typography>
        <Input
          value={localPage}
          onChange={handlePageChange}
          onBlur={handlePageOnBlur}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitPage(event?.target?.value);
            }
          }}
          inputWrapperClassName="min-w-[30px] items-center [&>div]:justify-center w-fit h-6 !p-0 !pr-1 !pl-2 rounded-[4px]"
          size={String(localPage ?? '').length || 1}
          style={{ width: `${Math.max(1, String(localPage ?? '').length)}ch` }}
          inputClassName="font-medium text-xs leading-[14px] text-neutral-700"
        />
        <Typography
          variant="label"
          size="small"
          weight="medium"
          color="neutral-500"
          className="leading-[14px]">
          of
        </Typography>
        <Typography
          variant="label"
          size="small"
          weight="medium"
          color="neutral-700"
          className="leading-[14px]">
          {formatLargeNumber(totalPages)}
        </Typography>
      </div>
      <Button
        variant="tertiary"
        size="small"
        onClick={handleFirst}
        isDisabled={isFirstPage}
        icon="chevron-first-linear"
        iconShade={isFirstPage ? 400 : 700}
        color="neutral"
        iconSize={16}
        className="h-6 w-6 p-0 disabled:bg-neutral-75"
      />
      <Button
        variant="tertiary"
        size="small"
        onClick={handlePrevious}
        isDisabled={isFirstPage}
        icon="chevron-left-linear"
        iconShade={isFirstPage ? 400 : 700}
        color="neutral"
        iconSize={16}
        className="h-6 w-6 p-0 disabled:bg-neutral-75"
      />
      <Button
        variant="tertiary"
        size="small"
        onClick={handleNext}
        isDisabled={isLastPage}
        icon="chevron-right-linear"
        iconShade={isLastPage ? 400 : 700}
        color="neutral"
        iconSize={16}
        className="h-6 w-6 p-0 disabled:bg-neutral-75"
      />
      <Button
        variant="tertiary"
        size="small"
        onClick={handleLast}
        isDisabled={isLastPage}
        icon="chevron-last-linear"
        iconShade={isLastPage ? 400 : 700}
        color="neutral"
        iconSize={16}
        className="h-6 w-6 p-0 disabled:bg-neutral-75"
      />
    </div>
  );
};

export default CustomPagination;
